import React from "react";
import { logClientError } from "../lib/clientLog";

// Detects failures to load a lazily-imported route chunk (common after a redeploy when a
// stale index.html / service worker points at hashed files that no longer exist).
const isChunkError = (err) => {
  const m = (err && (err.message || String(err))) || "";
  return /Loading chunk|dynamically imported module|Importing a module script failed|ChunkLoadError|error loading dynamically imported/i.test(m);
};

const RELOAD_FLAG = "tm_chunk_reloaded";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, reloading: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Silently record the crash to the DB for our weekly review — never shown to the user.
    const message = (error && (error.message || String(error))) || "Unknown error";
    const stack = (error && error.stack) || "";
    const componentStack = (info && info.componentStack) || "";
    try {
      logClientError({ source: "react-error-boundary", message, stack, context: componentStack.slice(0, 1000) });
    } catch (e) { /* ignore */ }

    let alreadyTried = false;
    try { alreadyTried = sessionStorage.getItem(RELOAD_FLAG) === "1"; } catch (e) {}
    if (isChunkError(error) && !alreadyTried) {
      try { sessionStorage.setItem(RELOAD_FLAG, "1"); } catch (e) {}
      this.setState({ reloading: true });
      (async () => {
        try {
          if ("serviceWorker" in navigator) {
            const regs = await navigator.serviceWorker.getRegistrations();
            await Promise.all(regs.map((r) => r.unregister()));
          }
          if (window.caches) {
            const keys = await caches.keys();
            await Promise.all(keys.map((k) => caches.delete(k)));
          }
        } catch (e) {}
        window.location.reload();
      })();
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.state.reloading) {
      return <div className="min-h-screen flex items-center justify-center text-[#737373] text-sm">Updating…</div>;
    }
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center bg-[#FAFAF8]">
        <div className="text-lg font-semibold text-[#1A1A1A]">Something went wrong</div>
        <p className="text-sm text-[#737373] max-w-xs">Sorry about that — please reload and try again. If it keeps happening, close the app completely and reopen it from your home screen.</p>
        <button
          data-testid="error-reload-btn"
          onClick={() => { try { sessionStorage.removeItem(RELOAD_FLAG); } catch (e) {} window.location.reload(); }}
          className="bg-[#1A1A1A] text-white rounded-lg px-6 py-2.5 text-sm font-semibold"
        >
          Reload
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
